import type { NormPoint, RegionId } from './types';
import { pointInPolygon } from './geometry/pointInPolygon';
import type { RegionShape } from './geometry/turtle';

/**
 * Region hit-testing. Given a normalized point, returns the id of the region
 * that contains it, or null if the point is outside every region (outside the
 * turtle). Overlaps are resolved FRONT-TO-BACK: the test order is the reverse
 * of the back-to-front draw order, so the visually top-most region wins.
 */
export class RegionHitTester {
  private readonly frontToBack: RegionShape[];

  /**
   * @param regions regions in BACK-TO-FRONT draw order (as authored). The tester
   *                reverses them internally for hit resolution.
   */
  constructor(regions: readonly RegionShape[]) {
    this.frontToBack = [...regions].reverse();
  }

  /** Returns the region containing `p`, or null if none. Boundary counts as inside. */
  hit(p: NormPoint): RegionId | null {
    for (const region of this.frontToBack) {
      for (const poly of region.polygons) {
        if (pointInPolygon(p, poly)) return region.id;
      }
    }
    return null;
  }

  /** Convenience predicate: is the point inside ANY region (i.e. on the turtle)? */
  isOnArtwork(p: NormPoint): boolean {
    return this.hit(p) !== null;
  }
}
